import {
  TRoleListFilters,
  TPermissionListFilters,
  TBulkPermissionAssignmentRequest,
  TApiRawError,
} from "./role-management.types";

/**
 * Utility functions for role management
 */

/**
 * Transform role list filters to query parameters
 */
export const buildRoleListQuery = (
  filters: TRoleListFilters,
): URLSearchParams => {
  const queryParams = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams;
};

/**
 * Transform permission list filters to query parameters
 */
export const buildPermissionListQuery = (
  filters: TPermissionListFilters,
): URLSearchParams => {
  const queryParams = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams;
};

/**
 * Handle API errors with user-friendly messages
 */
export const handleApiError = (error: TApiRawError): string => {
  // Check for specific error types
  if (error?.response?.status === 404) {
    return "The requested resource was not found.";
  }

  if (error?.response?.status === 403) {
    return "You do not have permission to perform this action.";
  }

  if (error?.response?.status === 400) {
    return "Invalid request. Please check your input and try again.";
  }

  if (
    error?.response &&
    typeof error.response.status === "number" &&
    error.response.status >= 500
  ) {
    return "A server error occurred. Please try again later.";
  }

  // Network errors
  if (error?.code === "NETWORK_ERROR" || !error?.response) {
    return "Network error. Please check your connection and try again.";
  }

  // Default error message
  return "An unexpected error occurred. Please try again.";
};

/**
 * Validate role ID format
 */
export const validateRoleId = (roleId: string): boolean => {
  return typeof roleId === "string" && roleId.trim().length > 0;
};

/**
 * Validate permission assignment request
 */
export const validateAssignmentRequest = (
  data: TBulkPermissionAssignmentRequest,
): {
  valid: boolean;
  errors: string[];
} => {
  const errors: string[] = [];

  if (!validateRoleId(data.role_id)) {
    errors.push("Valid role ID is required");
  }

  if (!Array.isArray(data.permission_ids) || data.permission_ids.length === 0) {
    errors.push("At least one permission ID is required");
  }

  if (!["assign", "remove"].includes(data.action)) {
    errors.push('Action must be either "assign" or "remove"');
  }

  // Validate permission IDs are non-empty values (supports internal_id strings)
  if (Array.isArray(data.permission_ids)) {
    const invalidIds = data.permission_ids.filter((id) => {
      if (typeof id === "number") return !Number.isInteger(id) || id <= 0;
      if (typeof id === "string") return id.trim().length === 0;
      return true;
    });
    if (invalidIds.length > 0) {
      errors.push("All permission IDs must be valid internal IDs");
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Performance monitoring utilities
 */
const fetchStartTimes = new Map<string, number[]>();

export const measureDataFetching = {
  startFetch: (operation: string) => {
    const activeStartTimes = fetchStartTimes.get(operation) ?? [];
    activeStartTimes.push(performance.now());
    fetchStartTimes.set(operation, activeStartTimes);
  },

  endFetch: (operation: string, success: boolean, resultCount?: number) => {
    const activeStartTimes = fetchStartTimes.get(operation) ?? [];
    const startTime = activeStartTimes.shift();

    if (activeStartTimes.length === 0) {
      fetchStartTimes.delete(operation);
    } else {
      fetchStartTimes.set(operation, activeStartTimes);
    }

    void startTime;
    void success;
    void resultCount;
  },
};
